import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Heart, MessageCircle, Bookmark, Share2, X, Play, Trash2,
  ChevronLeft, ChevronRight, Images,
} from "lucide-react";

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export function MediaFrame({ item, className = "", rounded = "rounded-xl", inline = false }) {
  if (!item) return null;
  if (item.type === "video") {
    return inline ? (
      <video src={item.url} poster={item.poster_url || undefined} controls playsInline preload="metadata"
        className={`w-full bg-black ${rounded} ${className}`} data-testid="profile-post-video"/>
    ) : (
      <div className={`relative ${rounded} overflow-hidden ${className}`}>
        {item.poster_url
          ? <img src={item.poster_url} alt="" className="w-full h-full object-cover"/>
          : <div className="w-full h-full bg-[#222321]"/>}
        <span className="absolute inset-0 grid place-items-center">
          <span className="w-11 h-11 rounded-full bg-black/55 backdrop-blur grid place-items-center">
            <Play size={18} className="text-white ml-0.5"/>
          </span>
        </span>
      </div>
    );
  }
  return <img src={item.url} alt="" className={`w-full h-full object-cover ${rounded} ${className}`}/>;
}

/** Grid tile — media first, caption restrained. */
export function ProfilePostTile({ post, onOpen }) {
  const cover = post.media?.[0];
  return (
    <button onClick={() => onOpen(post)}
      className="group block w-full text-left mb-5 break-inside-avoid rounded-2xl bg-white border border-[#D3DAD5] overflow-hidden hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-16px_rgba(34,35,33,0.35)] transition-transform duration-200"
      data-testid={`profile-post-tile-${post.id}`}>
      <div className="relative">
        <MediaFrame item={cover} rounded="rounded-none" className="aspect-[4/5] w-full"/>
        {post.media?.length > 1 && (
          <span className="absolute top-2.5 right-2.5 text-[11px] font-semibold text-white bg-black/45 backdrop-blur px-2 py-0.5 rounded-full flex items-center gap-1">
            <Images size={11}/> {post.media.length}
          </span>
        )}
      </div>
      <div className="px-3.5 py-3">
        {post.category && (
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#2E6B57]">{post.category}</span>
        )}
        {post.caption && (
          <p className="text-sm text-[#222321]/80 leading-snug mt-1 line-clamp-2">{post.caption}</p>
        )}
        <div className="flex items-center gap-3.5 mt-2.5 text-[11px] text-[#222321]/45 font-semibold">
          <span className="flex items-center gap-1"><Heart size={12} className={post.liked ? "fill-[#2E6B57] text-[#2E6B57]" : ""}/> {post.likes_count ?? 0}</span>
          <span className="flex items-center gap-1"><MessageCircle size={12}/> {post.comments?.length ?? post.comments_count ?? 0}</span>
          <span className="ml-auto">{when(post.created_at)}</span>
        </div>
      </div>
    </button>
  );
}

/** Detail view — full media, caption, category, comments, like/save/share. */
export function ProfilePostModal({ postId, onClose, onChanged, canInteract = true }) {
  const [post, setPost] = useState(null);
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/profile-posts/${postId}`).then(r => { setPost(r.data.post); setIdx(0); }).catch(() => onClose());
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [postId]);

  if (!post) {
    return (
      <div className="fixed inset-0 z-50 bg-[#222321]/70 grid place-items-center p-4" onClick={onClose}>
        <div className="text-white text-sm">Loading…</div>
      </div>
    );
  }

  const media = post.media || [];
  const act = async (fn) => { if (busy) return; setBusy(true); try { await fn(); } finally { setBusy(false); } };

  const like = () => act(async () => {
    const { data } = await api.post(`/profile-posts/${post.id}/like`);
    setPost(p => ({ ...p, liked: data.liked, likes_count: data.likes_count }));
    onChanged?.();
  });
  const save = () => act(async () => {
    const { data } = await api.post(`/profile-posts/${post.id}/save`);
    setPost(p => ({ ...p, saved: data.saved }));
    toast.success(data.saved ? "Saved" : "Removed from saved");
  });
  const share = async () => {
    const url = `${window.location.origin}/client/${post.user_id}`;
    try {
      if (navigator.share) await navigator.share({ title: "LIMYA", url });
      else { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); }
    } catch { /* dismissed */ }
  };
  const comment = () => act(async () => {
    if (!text.trim()) return;
    await api.post(`/profile-posts/${post.id}/comment`, { text });
    setText("");
    await load();
    onChanged?.();
  });
  const remove = () => act(async () => {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    await api.delete(`/profile-posts/${post.id}`);
    toast.success("Post deleted");
    onChanged?.();
    onClose();
  });

  return (
    <div className="fixed inset-0 z-50 bg-[#222321]/75 backdrop-blur-sm overflow-y-auto p-0 sm:p-6" onClick={onClose} data-testid="profile-post-modal">
      <div className="bg-[#FFFFFF] w-full max-w-4xl mx-auto sm:rounded-2xl overflow-hidden flex flex-col lg:flex-row min-h-full sm:min-h-0" onClick={e => e.stopPropagation()}>
        <div className="relative bg-[#222321] lg:w-[58%] shrink-0">
          <MediaFrame item={media[idx]} rounded="rounded-none" inline={media[idx]?.type === "video"} className="max-h-[70vh] object-contain w-full"/>
          {media.length > 1 && (
            <>
              <button onClick={() => setIdx(i => (i - 1 + media.length) % media.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 text-white grid place-items-center" data-testid="post-media-prev"><ChevronLeft size={18}/></button>
              <button onClick={() => setIdx(i => (i + 1) % media.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 text-white grid place-items-center" data-testid="post-media-next"><ChevronRight size={18}/></button>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                {media.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-white" : "bg-white/40"}`}/>)}
              </div>
            </>
          )}
        </div>

        <div className="flex-1 flex flex-col p-5 sm:p-6 min-w-0">
          <div className="flex items-start gap-3 mb-4">
            <span className="w-10 h-10 rounded-full bg-[#E9F0EC] grid place-items-center font-bold text-[#245544] overflow-hidden shrink-0">
              {post.author?.avatar_url ? <img src={post.author.avatar_url} alt="" className="w-full h-full object-cover"/> : post.author?.name?.[0]}
            </span>
            <div className="min-w-0">
              <p className="font-bold text-[#222321] truncate" style={{ fontFamily: "Outfit" }}>{post.author?.name}</p>
              <p className="text-xs text-[#222321]/45">{when(post.created_at)}{post.author?.city ? ` · ${post.author.city}` : ""}</p>
            </div>
            <button onClick={onClose} className="ml-auto p-1.5 rounded-full hover:bg-black/5" data-testid="post-modal-close"><X size={18}/></button>
          </div>

          {post.category && (
            <span className="self-start text-[10px] uppercase tracking-[0.14em] font-bold text-[#2E6B57] mb-2">{post.category}</span>
          )}
          {post.caption && <p className="text-[15px] text-[#222321]/85 leading-relaxed whitespace-pre-wrap mb-5" data-testid="post-modal-caption">{post.caption}</p>}

          {canInteract && (
            <div className="flex items-center gap-1.5 pb-4 border-b border-[#D3DAD5]">
              <button onClick={like} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-black/5 text-sm font-semibold" data-testid="post-like-btn">
                <Heart size={16} className={post.liked ? "fill-[#2E6B57] text-[#2E6B57]" : ""}/> {post.likes_count || 0}
              </button>
              <button onClick={save} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-black/5 text-sm font-semibold" data-testid="post-save-btn">
                <Bookmark size={16} className={post.saved ? "fill-[#2E6B57] text-[#2E6B57]" : ""}/> {post.saved ? "Saved" : "Save"}
              </button>
              <button onClick={share} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-black/5 text-sm font-semibold" data-testid="post-share-btn">
                <Share2 size={16}/> Share
              </button>
              {post.is_owner && (
                <button onClick={remove} className="ml-auto p-2 rounded-full hover:bg-red-50 text-red-600" data-testid="post-delete-btn"><Trash2 size={15}/></button>
              )}
            </div>
          )}

          <div className="flex-1 py-4 space-y-3 min-h-[60px]">
            {(post.comments || []).length === 0 && <p className="text-xs text-[#222321]/40">No comments yet.</p>}
            {(post.comments || []).map(c => (
              <div key={c.id} className="flex gap-2.5" data-testid={`post-comment-${c.id}`}>
                <span className="w-7 h-7 rounded-full bg-[#F5F7F5] grid place-items-center text-[11px] font-bold shrink-0 overflow-hidden">
                  {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-full h-full object-cover"/> : c.name?.[0]}
                </span>
                <p className="text-sm text-[#222321]/80 leading-snug">
                  <span className="font-semibold text-[#222321]">{c.name}</span> {c.text}
                </p>
              </div>
            ))}
          </div>

          {canInteract && (
            <div className="flex items-center gap-2 pt-3 border-t border-[#D3DAD5]">
              <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && comment()}
                placeholder="Add a comment…" className="flex-1 px-3 py-2 text-sm rounded-full border border-[#D3DAD5] outline-none focus:ring-2 focus:ring-[#2E6B57]"
                data-testid="post-comment-input"/>
              <button onClick={comment} disabled={!text.trim() || busy} className="px-4 py-2 text-sm font-semibold rounded-full bg-[#245544] text-white disabled:opacity-40" data-testid="post-comment-submit">Post</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Feed card for a followed Client's Profile Post. */
export function ProfilePostFeedCard({ post, onOpen }) {
  const navigate = useNavigate();
  const cover = post.media?.[0];
  return (
    <article className="bg-white rounded-2xl border border-[#D3DAD5] overflow-hidden mb-5" data-testid={`feed-profile-post-${post.id}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="w-9 h-9 rounded-full bg-[#E9F0EC] grid place-items-center font-bold text-[#245544] overflow-hidden shrink-0">
          {post.author?.avatar_url ? <img src={post.author.avatar_url} alt="" className="w-full h-full object-cover"/> : post.author?.name?.[0]}
        </span>
        <div className="min-w-0">
          <button onClick={() => navigate(`/client/${post.user_id}`)} className="font-semibold text-sm hover:underline block truncate" data-testid={`feed-profile-author-${post.id}`}>
            {post.author?.name}
          </button>
          <p className="text-[11px] text-[#222321]/45">Neighbor · {when(post.created_at)}</p>
        </div>
        {post.category && <span className="ml-auto text-[10px] uppercase tracking-[0.12em] font-bold text-[#2E6B57] shrink-0">{post.category}</span>}
      </div>
      <button onClick={() => onOpen(post)} className="block w-full" data-testid={`feed-profile-open-${post.id}`}>
        <MediaFrame item={cover} rounded="rounded-none" className="w-full max-h-[520px] aspect-[4/3]"/>
      </button>
      {post.caption && <p className="px-4 py-3 text-sm text-[#222321]/80 leading-relaxed">{post.caption}</p>}
      <div className="px-4 pb-3 flex items-center gap-4 text-xs text-[#222321]/50 font-semibold">
        <span className="flex items-center gap-1"><Heart size={13} className={post.liked ? "fill-[#2E6B57] text-[#2E6B57]" : ""}/> {post.likes_count || 0}</span>
        <span className="flex items-center gap-1"><MessageCircle size={13}/> {post.comments_count || 0}</span>
      </div>
    </article>
  );
}
